import React, { useEffect, useState } from "react";
import styles from "./SearchLogs.module.scss";
import AdminHeader from "../../../Components/AdminHeader/AdminHeader";
import { getSearchLogs } from "../../../Redux/Stats/StatsActions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../Redux/store";
import toast from "react-hot-toast";
import InlineLoader from "../../../Components/InlineLoader/InlineLoader";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import TextLogModal from "../../../Components/TextLogModal/TextLogModal";
import { DateTime } from "luxon";

export interface TextLogsType {
  awsMessageId: string;
  awsMessageResponse: {
    status: string;
    delivery: {
      mcc: number;
      mnc: number;
      smsType: string;
      priceInUSD: number;
      destination: string;
      dwellTimeMs: number;
      phoneCarrier: string;
      providerResponse: string;
      numberOfMessageParts: number;
      dwellTimeMsUntilDeviceAck: number;
    };
    notification: {
      messageId: string;
      timestamp: string;
    };
  };
  body: string;
  createdAt: string;
  id: number;
  number: string;
  phoneNumber: string;
  triggerType: string;
  updatedAt: string;
  token: string;
}

const columnDefs: ColDef<TextLogsType>[] = [
  {
    headerName: "Number",
    field: "number",
    flex: 1,
    maxWidth: 200,
  },
  {
    headerName: "Phone Number",
    field: "phoneNumber",

    maxWidth: 150,
  },
  {
    headerName: "Body",
    field: "body",
    flex: 1,
    minWidth: 200,
    filter: false,
    sortable: false,
    cellStyle: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      overflowWrap: "break-word",
      lineHeight: "1.5",
      padding: "10px",
      textAlign: "left",
    },
  },

  {
    headerName: "Created At",
    field: "createdAt",
    flex: 1,
    maxWidth: 200,
    minWidth: 150,
    valueGetter: (params) => {
      return params?.data?.createdAt
        ? DateTime.fromISO(params?.data?.createdAt, {
            zone: "America/New_York",
          }).toFormat("MM/dd/yyyy hh:mm a")
        : "";
    },
  },

  {
    headerName: "Trigger Type",
    field: "triggerType",
    flex: 1,
    maxWidth: 130,
  },
  {
    headerName: "Token",
    field: "token",
    flex: 1,
    maxWidth: 100,
    filter: false,
    sortable: false,
    valueGetter: (params) => {
      if (params?.data?.token) {
        return params?.data?.token;
      }
      return "N/A";
    },
  },

  {
    headerName: "Status",
    flex: 1,
    width: 100,
    valueGetter: (params) => {
      if (params?.data?.awsMessageResponse) {
        return params?.data?.awsMessageResponse?.status;
      }
      return "-";
    },
  },
  {
    headerName: "AWS Carrier Message",
    field: "awsMessageResponse",
    flex: 1,
    minWidth: 200,
    filter: false,
    sortable: false,
    valueGetter: (params) => {
      if (params?.data?.awsMessageResponse) {
        return params?.data?.awsMessageResponse?.delivery?.providerResponse;
      }
      return "-";
    },
    cellStyle: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      overflowWrap: "break-word",
      lineHeight: "1.5",
      padding: "10px",
      textAlign: "left",
    },
  },
];

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  suppressPaste: true,
  suppressNavigable: true,
  suppressMovable: true,
  wrapText: true,
  autoHeight: true,
};

function SearchLogs() {
  const dispatch = useDispatch<AppDispatch>();
  const { search_logs_loading, search_logs_data } = useSelector(
    (state: RootState) => state.stats
  );
  const [search, setSearch] = useState("2566266652");
  const [rowData, setRowData] = useState<TextLogsType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [selectedRowData, setSelectedRowData] = useState<TextLogsType | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const searchHandler = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (search) {
      if (!search_logs_loading) {
        dispatch(getSearchLogs(search));
      }
    } else {
      toast.error("Please enter a search term");
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  };

  const onRowClicked = (event: RowClickedEvent<TextLogsType>) => {
    if (event?.data) {
      setSelectedRowData(event.data);
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    if (search_logs_data) {
      setRowData(search_logs_data);
    }
  }, [search_logs_data]);

  const refreshHandler = () => {
    if (!search_logs_loading) {
      dispatch(getSearchLogs(search));
    }
  };

  return (
    <React.Fragment>
      <TextLogModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        selectedRowData={selectedRowData}
      />

      <div className={styles.mainContainer}>
        <AdminHeader />
        <div className={styles.bodyContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Search Logs</h1>
          </div>
          <form className={styles.searchContainer}>
            <div className={styles.leftContainer}>
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  placeholder="Search"
                  className={styles.input}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className={styles.buttonContainer}>
                <button
                  type="submit"
                  className={styles.button}
                  onClick={searchHandler}
                >
                  Search
                </button>
              </div>
            </div>
            <div className={styles.rightContainer}>
              <div className={styles.buttonContainer}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={refreshHandler}
                >
                  Refresh
                </button>
              </div>
            </div>
          </form>
          {search_logs_loading && (
            <div className={styles.loadingContainer}>
              <InlineLoader color="#000000" />
            </div>
          )}

          {Array.isArray(search_logs_data) && search_logs_data.length === 0 && (
            <div className={styles.noLogsContainer}>
              <p className={styles.noLogsText}>No logs found</p>
            </div>
          )}

          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={false}
              animateRows={true}
              domLayout="autoHeight"
              onRowClicked={onRowClicked}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default SearchLogs;
